import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronDown, ChevronUp, Library } from 'lucide-react';
import { api } from '@/lib/api/client';
import { FileUploader } from '@/components/forms/FileUploader';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import { useT } from '@/lib/i18n/useT';
import { cn } from '@/lib/cn';

interface WheelLogo {
  name: string;
  url: string;
}

/**
 * Wheel-logo input. Combines the existing free-form uploader with a
 * curated library served from `GET /api/v1/upload/wheel-logos`. Ops add
 * new logos by dropping files into `ox-platform/uploads/wheel-logos/` —
 * no migration, no rebuild required.
 *
 * The `value` is the canonical image url (relative or absolute) regardless
 * of which surface produced it; downstream code already knows how to
 * resolve it via `resolveImageUrl`.
 */
export function WheelLogoPicker({
  value,
  onChange,
  onUploadingChange,
}: {
  value: string | null;
  onChange: (next: string | null) => void;
  onUploadingChange?: (uploading: boolean) => void;
}) {
  const t = useT();
  const [libraryOpen, setLibraryOpen] = useState(false);

  return (
    <div className="space-y-2">
      <FileUploader
        value={value}
        onChange={onChange}
        onUploadingChange={onUploadingChange}
        preset="logo"
      />

      <button
        type="button"
        onClick={() => setLibraryOpen((v) => !v)}
        aria-expanded={libraryOpen}
        className="flex w-full items-center justify-between rounded-md border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-lavender)] transition-colors hover:border-[var(--color-xk-accent)]"
      >
        <span className="inline-flex items-center gap-1.5">
          <Library className="h-3.5 w-3.5" />
          {t('stock.dialog.field.wheelLogoLibrary')}
        </span>
        {libraryOpen ? (
          <ChevronUp className="h-3 w-3" />
        ) : (
          <ChevronDown className="h-3 w-3" />
        )}
      </button>

      {libraryOpen && (
        <LibraryGrid value={value} onPick={(url) => onChange(url)} />
      )}
    </div>
  );
}

function LibraryGrid({
  value,
  onPick,
}: {
  value: string | null;
  onPick: (url: string) => void;
}) {
  const t = useT();
  const { data, isLoading } = useQuery<WheelLogo[]>({
    queryKey: ['wheel-logos'],
    queryFn: () => api.get<WheelLogo[]>('/upload/wheel-logos'),
    staleTime: 5 * 60_000,
  });

  if (isLoading) {
    return (
      <p className="px-3 py-2 text-[11px] text-[var(--color-xk-text-muted)]">
        {t('common.loading')}
      </p>
    );
  }
  if (!data || data.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-[var(--color-xk-border)] px-3 py-4 text-center text-[11px] text-[var(--color-xk-text-muted)]">
        {t('stock.dialog.field.wheelLogoLibraryEmpty')}
      </p>
    );
  }

  return (
    <div className="grid max-h-56 grid-cols-4 gap-1.5 overflow-y-auto rounded-md border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] p-2 scrollbar-thin">
      {data.map((logo) => {
        const selected = value === logo.url;
        return (
          <button
            key={logo.name}
            type="button"
            onClick={() => onPick(logo.url)}
            title={logo.name}
            aria-pressed={selected}
            className={cn(
              'relative grid aspect-square place-items-center overflow-hidden rounded-md border bg-[var(--color-xk-surface)] p-1 transition-colors',
              selected
                ? 'border-[var(--color-xk-accent)] ring-2 ring-[var(--color-xk-accent)]'
                : 'border-[var(--color-xk-border)] hover:border-[var(--color-xk-accent)]',
            )}
          >
            <img
              src={resolveImageUrl(logo.url)}
              alt={logo.name}
              className="max-h-full max-w-full object-contain"
            />
            {selected && (
              <Check className="absolute right-0.5 top-0.5 h-3 w-3 rounded-full bg-[var(--color-xk-accent)] p-0.5 text-[#0A0A0A]" />
            )}
          </button>
        );
      })}
    </div>
  );
}
